import { clamp } from "./ShareCardLayout";
import { constrainImageElement } from "./ShareCardElements";
import { TOOLTIP_BODY_FONT, loadTooltipAssets } from "./ShareTooltip";

export const DEFAULT_NOTE_TEXT_SIZE = 28;
export const NOTE_SAMPLE = { graphic: "note", text: "Add a note about your cards.", textSize: DEFAULT_NOTE_TEXT_SIZE * 1.4 };
const WIDTH = 512, HEIGHT = 502;
const LEFT = 32, TOP = 80, BOTTOM = HEIGHT - 35;
// Follow the usable paper edge above the curl, with a little breathing room.
const rightAt = (y) => WIDTH - 32 - Math.max(0, y - 370) * 0.9;
let assetPromise;

export const loadNoteAssets = () => {
  if (!assetPromise) {
    const paper = new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not load the note paper. Please try again."));
      image.src = "/images/share-editor/note.webp";
    });
    // Share the tooltip's loaded TTF font instead of baking text into an image.
    assetPromise = Promise.all([paper, loadTooltipAssets()]).then(([image]) => ({ paper: image })).catch((error) => {
      assetPromise = null;
      throw error;
    });
  }
  return assetPromise;
};

const wrapNoteText = (context, text, fontSize) => {
  const advance = fontSize * 1.3;
  const lines = [];
  const width = () => rightAt(Math.min(BOTTOM, TOP + (lines.length + 1) * advance)) - LEFT;
  context.font = `400 ${fontSize}px ${TOOLTIP_BODY_FONT}`;
  for (const paragraph of text.replace(/\r\n?/g, "\n").split("\n")) {
    let line = "";
    for (const word of paragraph.trim().split(/\s+/)) {
      const candidate = line ? `${line} ${word}` : word;
      if (context.measureText(candidate).width <= width()) { line = candidate; continue; }
      if (line) { lines.push(line); line = ""; }
      for (const character of Array.from(word)) {
        if (line && context.measureText(line + character).width > width()) { lines.push(line); line = ""; }
        line += character;
      }
    }
    lines.push(line);
  }
  return { lines, fontSize, advance, overflow: TOP + lines.length * advance > BOTTOM + 0.001 };
};

export const getNoteLayout = (element) => {
  const context = document.createElement("canvas").getContext("2d");
  const text = String(element.text || "").slice(0, 1200);
  const preferredSize = clamp(Number(element.textSize) || DEFAULT_NOTE_TEXT_SIZE, 10, DEFAULT_NOTE_TEXT_SIZE * 3.5);
  let layout = wrapNoteText(context, text, preferredSize);
  if (layout.overflow) {
    let low = 10, high = preferredSize;
    layout = wrapNoteText(context, text, low);
    if (!layout.overflow) {
      for (let step = 0; step < 8; step += 1) {
        const middle = (low + high) / 2;
        const candidate = wrapNoteText(context, text, middle);
        if (candidate.overflow) high = middle;
        else { low = middle; layout = candidate; }
      }
    }
  }
  return { ...layout, width: WIDTH, height: HEIGHT, left: LEFT, top: TOP, bottom: BOTTOM };
};

export const createNoteElement = (scene, topZ) => {
  const width = Math.min(scene.width * 0.32, scene.height * 0.44 * WIDTH / HEIGHT);
  return constrainImageElement({
    ...NOTE_SAMPLE, id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind: "image", label: "Note", x: scene.width * 0.5, y: scene.height * 0.5,
    width, height: width * HEIGHT / WIDTH, baseWidth: width, rotation: 0, z: topZ + 1,
    shadow: { enabled: true, color: "#000000", opacity: 0.55, blur: 12, offsetX: 2, offsetY: 7 },
  }, scene);
};

export const paintNoteCanvas = (canvas, element, assets, density = 1) => {
  const layout = getNoteLayout(element);
  const width = element.width || WIDTH, height = element.height || HEIGHT;
  const ratio = Math.min(density, 8192 / Math.max(width, height), Math.sqrt(16000000 / (width * height)));
  canvas.width = Math.max(1, Math.round(width * ratio));
  canvas.height = Math.max(1, Math.round(height * ratio));
  const context = canvas.getContext("2d");
  if (!context) return canvas;
  context.scale(canvas.width / WIDTH, canvas.height / HEIGHT);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(assets.paper, 0, 0, WIDTH, HEIGHT);
  context.save();
  context.beginPath();
  context.moveTo(LEFT, TOP);
  context.lineTo(rightAt(TOP), TOP);
  context.lineTo(rightAt(370), 370);
  context.lineTo(rightAt(BOTTOM), BOTTOM);
  context.lineTo(LEFT, BOTTOM);
  context.closePath();
  context.clip();
  context.font = `400 ${layout.fontSize}px ${TOOLTIP_BODY_FONT}`;
  context.fillStyle = "#000000";
  context.textAlign = "left";
  context.textBaseline = "alphabetic";
  layout.lines.forEach((line, index) => {
    const y = TOP + index * layout.advance;
    if (y + layout.advance <= BOTTOM + 0.001) context.fillText(line, LEFT, y + layout.fontSize);
  });
  context.restore();
  return canvas;
};

